import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { mockAccounts } from '@/mocks/accounts';
import { login as loginRequest, type LoginResult } from '@/services/authService';
import type { Account } from '@/types';

const SESSION_KEY = 'lb_session_account_id';

interface AuthContextValue {
  user: Account | null;
  isAuthenticated: boolean;
  initializing: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Account | null>(null);
  const [initializing, setInitializing] = useState(true);

  // Restore session from an opaque account id only (never a password).
  useEffect(() => {
    try {
      const id = window.sessionStorage.getItem(SESSION_KEY);
      if (id) {
        const account = mockAccounts.find((a) => a.id === id && a.status === 'approved');
        if (account) setUser(account);
        else window.sessionStorage.removeItem(SESSION_KEY);
      }
    } catch {
      // ignore storage errors
    } finally {
      setInitializing(false);
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await loginRequest(email, password);
    if (result.ok) {
      setUser(result.account);
      try {
        window.sessionStorage.setItem(SESSION_KEY, result.account.id);
      } catch {
        // ignore storage errors
      }
    }
    return result;
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    try {
      window.sessionStorage.removeItem(SESSION_KEY);
    } catch {
      // ignore storage errors
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: Boolean(user), initializing, login, logout }),
    [user, initializing, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}